import { createSelector } from '@reduxjs/toolkit';
import { CompanyType, GetApiDimensionsResponse, queryKeyFn } from '@sit/client-shared';
import { queryClient } from '@web/hooks/api/query/client';
import moment from 'moment';
import { adminManagerRegEx } from '../../constants/regularExpressions';
import { checkIfEntrySubmitted } from '../../containers/Timesheet/helpers';
import { getCompanyFromWebSessionStorage } from '../../helpers/session';
import { secondsToHoursFixed } from '../../helpers/time';
import { ReduxState } from '../../types/redux/state';
import { ClientsStoreItem } from '../../types/redux/stores/clients.types';
import { ProjectsStoreItem } from '../../types/redux/stores/projects.types';
import { TimesheetEntriesStoreItem } from '../../types/redux/stores/timesheetEntries.types';
import { BuildType, DevOverrides } from '../Development';

export * from './approvals';
export * from './autofill';

// To remove when we remove the saga that uses this (GenerateAutofill)
export const getCompanyData = () => {
  const companyId = getCompanyFromWebSessionStorage()?.id;
  const company: CompanyType | undefined = queryClient.getQueryData(
    queryKeyFn({
      path: '/companies',
      operationId: 'getApiCompany',
      variables: {
        queryParams: {
          companyId,
        },
      },
    }),
  );
  return company;
};

export const getClusterAssignModal = (state: ReduxState) => state.clusterAssignModal || {};
const getClusters = (state: ReduxState) => state.clusters || {};
export const getClustersData = (state: ReduxState) => state.clusters?.data || [];
const getTimesheet = (state: ReduxState) => state.timesheet || {};
const getTimesheetData = (state: ReduxState) => state.timesheet?.data || {};
const getTimesheetInitialLoad = (state: ReduxState) => state.timesheet?.initialLoad || false;
export const getTimesheetId = (state: ReduxState) => getTimesheetData(state).id;
const getTimesheetEntries = (state: ReduxState) => state.timesheetEntries || {};
const getTimesheetEntriesInitialLoad = (state: ReduxState) => state.timesheetEntries?.initialLoad || false;
export const getTimesheetEntriesData = (state: ReduxState) => state.timesheetEntries?.data || {};
const getTimesheetRows = (state: ReduxState) => state.timesheetRows || {};
const getTimesheetRowsInitialLoad = (state: ReduxState) => state.timesheetRows?.initialLoad || false;
const getTimesheetRowsData = (state: ReduxState) => state.timesheetRows?.data || [];
const getToken = (state: ReduxState) => state.user.token || null;
const getRefreshToken = (state: ReduxState) => state.user.refreshToken || null;
const getUser = (state: ReduxState) => state.user || {};
export const getUserData = (state: ReduxState) => state.user?.data || {};
const getShowCustomer = (_state: ReduxState) => getCompanyData()?.showCustom || false;
const getShowProject = (_state: ReduxState) => getCompanyData()?.showProject || false;
// Dimension related
const getClientsData = (state: ReduxState) => state.clients?.data || [];
const getProjectsPerClientId = (state: ReduxState) => state.projects?.perClientId || {};
const getTasksPerProjectId = (state: ReduxState) => state.tasks?.perProjectId || {};
const getItemsData = (state: ReduxState) => state.items?.data || [];
export const getItemsInitialLoad = (state: ReduxState) => state.items?.initialLoad || false;
export const getItemsLoading = (state: ReduxState) => state.items?.loading || false;
const getBuildType = (state: ReduxState) => state.development.buildType;
const getDevOverrides = (state: ReduxState) => state.development.devOverrides;
const getTimesheetStatusStoreData = (state: ReduxState) => state.timesheetStatus;

// helpers
export const findDepartmentDimension = (dimensionsData: GetApiDimensionsResponse) =>
  (dimensionsData || []).find((d) => d.integrationMeta?.objectName === 'DEPARTMENT');
export const findLocationDimension = (dimensionsData: GetApiDimensionsResponse) =>
  (dimensionsData || []).find((d) => d.integrationMeta?.objectName === 'LOCATION');

export const selectIsAuthenticated = createSelector(getToken, (token) => token != null);
export const selectToken = createSelector(getToken, (token) => token);
export const selectRefreshToken = createSelector(getRefreshToken, (refreshToken) => refreshToken);

export const selectUser = createSelector(getUser, (user) => user);
export const selectUserData = createSelector(getUserData, (userData) => userData);
export const selectUserDataForPendo = createSelector(getUserData, (userData) => {
  if (!userData || !userData.companies || !userData.company) return null;
  const entityData = userData.companies.find((c) => c.id === userData.company.id);
  const companyEntityName = entityData?.parentId === null ? 'TOP LEVEL' : entityData?.name ?? 'NO_ENTITY_NAME';
  const companyName = userData.company.intacctCompanyId ?? 'NO_COMPANY_NAME';
  const companyId = userData.company.id ?? 'NO_COMPANY_ID';

  const result: Record<string, string | number | boolean | string[]> = {
    email: userData.email,
    full_name: userData.name,
    id: userData.id,
    position: userData.position,
    twoFactorEnabled: userData.twoFactorEnabled,
    username: userData.username,
    companyName,
    companyId,
    companyEntityName,
  };
  if (userData.role) result.role = userData.role;
  return result;
});
export const isAdminOrManagerSelector = createSelector(
  getUserData,
  (user) => user?.hasStaffTimesheets || (user.role && adminManagerRegEx.test(user.role)),
);
export const selectUserId = createSelector(getUserData, (userData) => userData.id);

/**
 *     Timesheet
 */
export const selectTimesheet = createSelector(getTimesheet, (timesheet) => timesheet);
export const selectTimesheetInitialLoad = createSelector(getTimesheetInitialLoad, (timesheetInitialLoad) => timesheetInitialLoad);
export const selectTimesheetData = createSelector(getTimesheetData, (data) => data);
export const selectTimesheetComponentInitialLoad = createSelector(
  getTimesheetInitialLoad,
  getTimesheetEntriesInitialLoad,
  getTimesheetRowsInitialLoad,
  (timesheetInitialLoad, timesheetEntriesInitialLoad, timesheetRowsInitialLoad) =>
    !!timesheetInitialLoad && !!timesheetEntriesInitialLoad && !!timesheetRowsInitialLoad,
);

/**
 *     Timesheet Rows
 */
export const selectTimesheetRows = createSelector(getTimesheetRows, (rows) => rows);
export const selectRowHasEntries = createSelector(
  (state: ReduxState, row: any) => row?.entries || [],
  (entries) => entries?.length > 0,
);
export const selectRowHasSubmittedEntries = createSelector(
  (state: ReduxState, row?: any) => row?.entries || [],
  (entries: TimesheetEntriesStoreItem[]) => entries.some((e) => checkIfEntrySubmitted(e)),
);
export const selectRowClusters = createSelector(
  getClustersData,
  (state: ReduxState, row: any) => row,
  (clustersData: any[], row) =>
    clustersData?.filter(
      (c) => c.timesheet_entry_id && (row.entries || []).some((e: any) => e.id === c.timesheet_entry_id && !c.miscellaneousReason),
    ),
);
export const selectIsLastRowWithEntries = createSelector(
  getTimesheetRowsData,
  (rows: any[]) => rows.filter((r) => r.entries?.length > 0).length === 1,
);
export const selectIsLastRow = createSelector(getTimesheetRowsData, (rows) => rows?.length === 1);
export const selectDeleteRowDisabled = createSelector(
  (state: ReduxState, row: any) => row,
  (row: any) => row?.entries?.some((e: TimesheetEntriesStoreItem) => checkIfEntrySubmitted(e)),
);

/**
 *     Entries
 */
export const selectTimesheetEntries = createSelector(getTimesheetEntries, (entries) => entries);
export const selectDisabledEntries = createSelector(getTimesheetEntries, (entries) => entries.disabledEntries);
export const selectNotAllEntriesSubmitted = createSelector(
  getTimesheetEntriesData,
  (entriesData) =>
    !Object.values(entriesData).some((e) => {
      return e?.status !== 'DRAFT';
    }),
);
export const selectEntriesDateTotal = createSelector(
  (state: ReduxState, day: moment.Moment) => day,
  getTimesheetEntriesData,
  (day, entriesData) => {
    return secondsToHoursFixed(
      Object.values(entriesData).reduce((acc, cur) => {
        if (moment(cur.date, 'YYYY-MM-DD').isSame(day.format('YYYY-MM-DD'))) {
          return acc + Number.parseInt(`${cur.duration || 0}`, 10);
        }
        return acc;
      }, 0),
    );
  },
);
/**
 *     Cluster Assign modal
 */
export const selectClusterAssignModal = createSelector(getClusterAssignModal, (cam) => cam);
export const selectShouldDisplayClusterAssignModal = createSelector(getClusterAssignModal, (cam) => !!cam.visible);
export const selectClusterOfClusterAssignModal = createSelector(
  getClusterAssignModal,
  getClustersData,
  (clusterAssignModal, clustersData) => clustersData.find((c) => c.id === clusterAssignModal.cluster.id) || clusterAssignModal.cluster,
);

/**
 *     Clients
 */
export const selectClientsData = createSelector(getClientsData, (clientsData) => clientsData);

/**
 *     Projects
 */
export const selectProjectsPerClientId = createSelector(
  getProjectsPerClientId,
  getShowCustomer,
  (state: ReduxState, clientId: ClientsStoreItem['id']) => clientId,
  (perClientId, showCustomer, clientId) => {
    if (!showCustomer) return perClientId.all?.data || [];
    if (!perClientId || !clientId) return [];
    return perClientId[clientId]?.data || [];
  },
);
/**
 * Tasks
 */
export const selectTasksPerProjectId = createSelector(
  getTasksPerProjectId,
  getShowProject,
  (state: ReduxState, projectId?: ProjectsStoreItem['id'] | null) => projectId,
  (perProjectId, showProject, projectId) => {
    if (!projectId || !perProjectId) return [];
    if (!showProject) return perProjectId.all?.data || [];
    return perProjectId[projectId]?.data || [];
  },
);

/**
 *     Dimensions
 */
export const selectItemsData = createSelector(getItemsData, (itemsData) => itemsData);

/**
 *     Company
 */

export const selectShowCustomer = createSelector(
  getTimesheetData,
  getCompanyData,
  (timesheetData, companyData) => timesheetData.showCustom ?? !!companyData?.showCustom,
);
export const selectShowProject = createSelector(
  getTimesheetData,
  getCompanyData,
  (timesheetData, companyData) => timesheetData.showProject ?? !!companyData?.showProject,
);
export const selectTimesheetDimensions = createSelector(getTimesheetData, getCompanyData, (timesheetData, companyData) => ({
  showBillable: timesheetData.showBillable === undefined ? !!companyData?.showBillable : timesheetData.showBillable,
  showCustom: timesheetData.showCustom === undefined ? !!companyData?.showCustom : timesheetData.showCustom,
  showDepartmentLocation:
    timesheetData.showDepartmentLocation === undefined ? !!companyData?.showDepartmentLocation : timesheetData.showDepartmentLocation,
  showItem: timesheetData.showItem === undefined ? !!companyData?.showItem : timesheetData.showItem,
  showProject: timesheetData.showProject === undefined ? !!companyData?.showProject : timesheetData.showProject,
  showTask: timesheetData.showTask === undefined ? !!companyData?.showTask : timesheetData.showTask,
  showTimeType: timesheetData.showTimeType === undefined ? !!companyData?.showTimeType : timesheetData.showTimeType,
}));

/**
 *     User
 */
export const selectIsAdmin = createSelector(getUserData, (userData) => userData?.role === 'admin' || userData?.hasStaffTimesheets || false);
export const selectCompanies = createSelector(getUserData, (userData) => userData.companies);
export const selectDefaultCompanyId = createSelector(getUserData, (userData) => userData.defaultCompanyId);

/**
 *     Clusters
 */
export const selectClusters = createSelector(getClusters, (clusters) => clusters);
export const selectBuildType = createSelector(getBuildType, (buildType): BuildType => {
  return buildType;
});

export const selectDevOverrides = createSelector(getDevOverrides, (overrides): DevOverrides => {
  return overrides;
});

export const selectIsTimesheetSaving = createSelector(
  getTimesheetStatusStoreData,
  ({ pendingResponses }): boolean => Object.keys(pendingResponses).length > 0,
);
